import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store';
import { canAccessSmartLocks } from './utils/access';
import apiClient from './api/client';
import Logo from './components/Logo';
import SessionExpiredModal from './components/SessionExpiredModal';

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);

  return null;
}

const Auth = lazy(() => import('./pages/Auth'));
const AdminAuth = lazy(() => import('./pages/AdminAuth'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const PropertyDetails = lazy(() => import('./pages/PropertyDetails'));
const RoomDetails = lazy(() => import('./pages/RoomDetails'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const GlobalAdminDashboard = lazy(() => import('./pages/GlobalAdminDashboard'));
const OrgAdminDashboard = lazy(() => import('./pages/OrgAdminDashboard'));
const BookingDocument = lazy(() => import('./pages/BookingDocument'));
const AvailabilityCalendar = lazy(() => import('./pages/AvailabilityCalendar'));
const RateCalendar = lazy(() => import('./pages/RateCalendar'));
const HousekeepingSubmit = lazy(() => import('./pages/HousekeepingSubmit'));
const CoGuestSubmit = lazy(() => import('./pages/CoGuestSubmit'));
const GuestVerify = lazy(() => import('./pages/GuestVerify'));
const ReportsDashboard = lazy(() => import('./pages/ReportsDashboard'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const About = lazy(() => import('./pages/About'));
const MessageTemplates = lazy(() => import('./pages/MessageTemplates'));
const AirbnbVerify = lazy(() => import('./pages/AirbnbVerify'));
const SmartLocks = lazy(() => import('./pages/SmartLocks'));
const LandingPage = lazy(() => import('./pages/LandingPage'));

function PageLoader() {
  return (
    <div className="min-h-screen bg-mesh-green flex flex-col items-center justify-center gap-5">
      <div className="animate-float-slow">
        <Logo size="lg" />
      </div>
      <div className="h-1 w-24 bg-white/15 overflow-hidden rounded-full">
        <div className="h-full w-1/3 bg-gold-300 rounded-full animate-pulse-soft" />
      </div>
    </div>
  );
}

// Single source of truth for "where does this role land when logged in".
function roleHome(role) {
  if (role === 'global_admin') return '/global-admin';
  if (role === 'org_admin' || role === 'employee') return '/org-admin';
  if (role === 'admin') return '/admin/dashboard';
  return '/dashboard';
}

// `allowedRoles` gates by role; `allow` is a predicate for finer-grained
// rules (e.g. a specific department) that a plain role list can't express.
function ProtectedRoute({ children, allowedRoles = null, allow = null }) {
  const { user, isAuthenticated, isInitializing } = useAuthStore();
  if (isInitializing) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const denied = allow
    ? !allow(user)
    : (allowedRoles && !allowedRoles.includes(user?.role));
  if (denied) {
    return <Navigate to={roleHome(user?.role)} replace />;
  }
  return children;
}

// Guards the login / staff sign-in screens: an already-authenticated user
// (customer or admin) must never land back on a sign-in page - not by typing
// the URL, not via the browser back button. They get bounced to their home.
// The only ways out of an authenticated session are an explicit sign-out or
// confirming the session-timeout modal, both of which clear isAuthenticated.
function PublicOnlyRoute({ children }) {
  const { user, isAuthenticated, isInitializing } = useAuthStore();
  if (isInitializing) return <PageLoader />;
  if (isAuthenticated) return <Navigate to={roleHome(user?.role)} replace />;
  return children;
}

function RootRedirect() {
  const { user, isAuthenticated, isInitializing } = useAuthStore();
  if (isInitializing) return <PageLoader />;
  // Public root = the customer booking home (browse & book in the fewest clicks).
  // The B2B/owner marketing site lives at /for-hotels.
  if (!isAuthenticated) return <Dashboard publicMode />;
  return <Navigate to={roleHome(user?.role)} replace />;
}

function App() {
  const { setUser, setTokens, setAuthenticated, setInitializing } = useAuthStore();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      apiClient.get('/auth/profile')
        .then((response) => {
          setUser(response.data.user);
          setAuthenticated(true);
        })
        .catch(() => {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        })
        .finally(() => {
          setInitializing(false);
        });
    } else {
      setInitializing(false);
    }
  }, [setUser, setAuthenticated, setTokens, setInitializing]);

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/for-hotels" element={<LandingPage />} />
          <Route path="/login" element={<PublicOnlyRoute><Auth /></PublicOnlyRoute>} />
          <Route path="/staff" element={<PublicOnlyRoute><AdminAuth /></PublicOnlyRoute>} />
          <Route path="/properties" element={<Dashboard publicMode />} />
          <Route path="/property/:propertyId" element={<PropertyDetails />} />
          <Route path="/property/:propertyId/room/:roomId" element={<RoomDetails />} />

          {/* Public token-based pages (no auth required) */}
          <Route path="/housekeeping/:bookingId" element={<HousekeepingSubmit />} />
          <Route path="/coguest/:token" element={<CoGuestSubmit />} />
          <Route path="/guest-verify/:token" element={<GuestVerify />} />
          <Route path="/airbnb-verify" element={<AirbnbVerify />} />

          {/* Public legal pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<About />} />

          {/* Root redirect */}
          <Route path="/" element={<RootRedirect />} />

          {/* Guest user dashboard */}
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={['user']}>
              <Dashboard />
            </ProtectedRoute>
          } />

          {/* Booking document */}
          <Route path="/booking/:bookingId/document" element={
            <ProtectedRoute>
              <BookingDocument />
            </ProtectedRoute>
          } />

          {/* Legacy admin dashboard */}
          <Route path="/admin/dashboard" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />

          {/* Global Admin */}
          <Route path="/global-admin" element={
            <ProtectedRoute allowedRoles={['global_admin']}>
              <GlobalAdminDashboard />
            </ProtectedRoute>
          } />

          {/* Org Admin + employees */}
          <Route path="/org-admin" element={
            <ProtectedRoute allowedRoles={['org_admin', 'employee', 'admin']}>
              <OrgAdminDashboard />
            </ProtectedRoute>
          } />

          {/* Availability Calendar */}
          <Route path="/calendar/:propertyId" element={
            <ProtectedRoute allowedRoles={['org_admin', 'admin', 'global_admin', 'employee']}>
              <AvailabilityCalendar />
            </ProtectedRoute>
          } />

          <Route path="/rate-calendar/:propertyId" element={
            <ProtectedRoute allowedRoles={['org_admin', 'admin', 'global_admin', 'employee']}>
              <RateCalendar />
            </ProtectedRoute>
          } />

          {/* Reports */}
          <Route path="/reports" element={
            <ProtectedRoute allowedRoles={['org_admin', 'admin', 'global_admin']}>
              <ReportsDashboard />
            </ProtectedRoute>
          } />

          {/* Message Templates */}
          <Route path="/message-templates" element={
            <ProtectedRoute allowedRoles={['org_admin', 'admin']}>
              <MessageTemplates />
            </ProtectedRoute>
          } />

          {/* Smart Locks (Tuya IoT onboarding) - management + reception only */}
          <Route path="/admin/smart-locks" element={
            <ProtectedRoute allow={canAccessSmartLocks}>
              <SmartLocks />
            </ProtectedRoute>
          } />

          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </Suspense>
      <SessionExpiredModal />
      <Toaster
        position="top-center"
        gutter={8}
        toastOptions={{
          duration: 3500,
          style: {
            background: '#0d0d0c',
            color: '#fff',
            fontSize: '13.5px',
            padding: '10px 14px',
            borderRadius: '12px',
            border: '1px solid #2a2926',
            boxShadow: '0 8px 24px -8px rgba(0,0,0,0.25)',
          },
          success: { iconTheme: { primary: '#1c6b41', secondary: '#fff' } },
          error: { iconTheme: { primary: '#dc2626', secondary: '#fff' } },
        }}
      />
    </Router>
  );
}

export default App;
